import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { toast } from "sonner";
import { ShoppingCart, Loader2, X, Check, Trash2, ChevronLeft, ChevronRight, CreditCard, Info as InfoIcon } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { useBasket } from "@/context/BasketContext";
import RichHtml from "@/components/ui/RichHtml";
import { ProductBadge } from "@/components/ProductBadge";
import { BecomeSupplierModal } from "@/components/BecomeSupplierModal";
import BillingIntervalToggle from "@/components/BillingIntervalToggle";
import { defaultInterval, formatIntervalPrice, isFreeProduct, symbolFor } from "@/utils/priceIntervals";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = getApiUrl();

// Tailwind-`md` sized viewport probe — anything ≤767px counts as mobile.
// Kept local so we don't drag in another dependency and can tune it per
// module later without touching a shared hook.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia("(max-width: 767px)");
    const on = (e) => setIsMobile(e.matches);
    // Safari <14 uses the legacy addListener API — fall back to keep
    // older devices working.
    if (mql.addEventListener) mql.addEventListener("change", on);
    else mql.addListener(on);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", on);
      else mql.removeListener(on);
    };
  }, []);
  return isMobile;
}

/**
 * Public "shop" module — renders the live product catalog as a table,
 * grouped by category (category name = section heading).
 * Config knobs:
 *  - title:            section heading (default "Shop")
 *  - subtitle:         optional short blurb
 *  - limit:            cap the number of products shown (0 or undefined = all)
 *  - default_category: category slug/id to filter to when no URL filter is set
 *
 * URL filter: appending `?category=<slug>` (or `?category=<category_id>`)
 * to the page URL overrides `default_category` and scopes the grid to a
 * single category. Used by marketing links from the admin categories page.
 */
export default function ProductsModule({ config = {} }) {
  const {
    title = "Shop",
    subtitle = "",
    limit = 0,
    default_category = "",
    show_category_line = false,
    show_short_description = true,
    show_description_preview = false,
    show_full_description = false,
    show_supplier = false,
    show_add_to_basket = true,
    table_border_color = "",
    layout = "table",
    image_scale_pct = 100,
    section_title_size,
    section_title_color,
    section_title_weight,
    section_title_font_family,
    module_title_size,
    module_title_color,
    module_title_weight,
    module_title_font_family,
    subtitle_size,
    subtitle_color,
    subtitle_weight,
    subtitle_font_family,
    module_title_align = "center",
    subtitle_align = "center",
    section_title_align = "center",
    product_name_align = "left",
    // Timeline density selector — pages with 3-4 categories look better
    // with the numbered chevrons; long shops want "slim"; single-category
    // pages can hide it entirely.
    timeline_style = "slim",
    // Prev/Next button size as % of the default (100 = default). Applied
    // via inline transform: scale(...) so labels and chevrons stay
    // proportional. Same value used on desktop and mobile.
    nav_button_scale_pct = 100,
    // Vertical offset (px) between the button and the top of the first
    // product card in the active slide. Positive → sits above the card;
    // negative → dips into the card area. Defaults to a small negative so
    // the pill hugs the card top instead of floating loose.
    nav_button_top_offset_px = 0,
    // Font size (px) for the "Previous" / "Next" / "Checkout" labels.
    // 0 = keep the built-in size that scales with `nav_button_scale_pct`.
    nav_font_size_px = 0,
    // Nav button foreground + background — empty string means "inherit
    // Site Styles brand primary", matching how every other module handles
    // colours. Admin picks a hex to override per instance.
    nav_bg_color = "",
    nav_text_color = "",
    // Per-viewport overrides. Empty / undefined = use the desktop value.
    // Admins set these to give phones a denser layout without changing
    // the wide-screen presentation.
    layout_mobile,
    timeline_style_mobile,
    image_scale_pct_mobile,
  } = config;
  // Resolve viewport-scoped values. Anything ≤767px counts as mobile;
  // this matches Tailwind's `md` breakpoint so the CMS previews line up
  // with the rendered pages.
  const isMobile = useIsMobile();
  const layoutResolved = (isMobile && layout_mobile) ? layout_mobile : layout;
  const timelineStyleResolved = (isMobile && timeline_style_mobile) ? timeline_style_mobile : timeline_style;
  const imageScalePctResolved = (isMobile && image_scale_pct_mobile != null) ? image_scale_pct_mobile : image_scale_pct;
  // Merged section-title style — falls through to the tailwind defaults
  // when no admin overrides are set. Applied to both layouts (carousel &
  // table) so the control acts consistently no matter which one is on.
  const sectionTitleStyle = {
    ...(section_title_size ? { fontSize: `${section_title_size}px` } : {}),
    ...(section_title_color ? { color: section_title_color } : {}),
    ...(section_title_weight ? { fontWeight: section_title_weight } : {}),
    ...(section_title_font_family ? { fontFamily: section_title_font_family } : {}),
  };
  // Same fall-through pattern for the module title ("Our Services", "Shop",
  // …) that renders at the very top of the module.
  const moduleTitleStyle = {
    ...(module_title_size ? { fontSize: `${module_title_size}px` } : {}),
    ...(module_title_color ? { color: module_title_color } : {}),
    ...(module_title_weight ? { fontWeight: module_title_weight } : {}),
    ...(module_title_font_family ? { fontFamily: module_title_font_family } : {}),
  };
  // And the module subtitle line just below it.
  const subtitleStyle = {
    ...(subtitle_size ? { fontSize: `${subtitle_size}px` } : {}),
    ...(subtitle_color ? { color: subtitle_color } : {}),
    ...(subtitle_weight ? { fontWeight: subtitle_weight } : {}),
    ...(subtitle_font_family ? { fontFamily: subtitle_font_family } : {}),
  };
  // Individually-alignable title / subtitle so an admin can pin the
  // module title to the left, keep the subtitle centred, etc.
  const ALIGN_CLASS = { left: "text-left", center: "text-center", right: "text-right" };
  const JUSTIFY_CLASS = { left: "justify-start", center: "justify-center", right: "justify-end" };
  const moduleTitleAlignCls = ALIGN_CLASS[module_title_align] || "text-center";
  const subtitleAlignCls = ALIGN_CLASS[subtitle_align] || "text-center";
  const sectionTitleAlignCls = ALIGN_CLASS[section_title_align] || "text-center";
  const productNameJustifyCls = JUSTIFY_CLASS[product_name_align] || "justify-start";
  const scale = Math.max(10, Math.min(100, Number(imageScalePctResolved) || 100));
  const location = useLocation();
  const navigate = useNavigate();
  const categoryFilter = useMemo(() => {
    const p = new URLSearchParams(location.search || "");
    // URL wins; fall back to the admin-configured default.
    return (p.get("category") || default_category || "").trim();
  }, [location.search, default_category]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoFallback, setLogoFallback] = useState("");
  const [detail, setDetail] = useState(null);   // product whose details modal is open
  const [slideIdx, setSlideIdx] = useState(0);  // carousel-mode current category
  const basket = useBasket();
  // Live lookup of every fetched product by ID — used to determine
  // "is this basket item actually free" from CURRENT pricing rather than a
  // flag captured at add-time (which would go stale if localStorage
  // persisted across sessions, or if it only ever reflected the auto-seed
  // effect's own — possibly wrong — guess about a product's price).
  const productsById = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.product_id] = p; });
    return map;
  }, [products]);

  // Centralised "add to basket" — enforces the one-billing-type-at-a-time
  // rule (Stripe can't mix a one-off payment with a subscription in one
  // Checkout session). Rather than blocking the add with an error, any
  // conflicting items (different billing interval) are seamlessly removed
  // first so the shopper's new pick just takes over. Free items
  // (isFreeProduct) are exempt — they tag along with any paid item at no
  // extra cost. Returns true/false so callers (e.g. the detail modal) know
  // whether to close themselves.
  const handleAdd = (product, interval) => {
    if (basket.has(product.product_id)) { toast("Already in your basket"); return false; }
    if (!isFreeProduct(product)) {
      const conflicts = basket.items.filter((i) => {
        if (i.product_id === product.product_id) return false;
        const p = productsById[i.product_id];
        return p && !isFreeProduct(p) && i.billing_interval !== interval;
      });
      conflicts.forEach((i) => basket.remove(i.product_id));
    }
    basket.add(product.product_id, interval);
    toast.success(`Added "${product.name}" to basket`);
    // Per-product opt-in: skip the basket drawer entirely and jump the
    // shopper straight into checkout with this item already in the cart.
    if (product.checkout_on_add) {
      navigate("/checkout");
    }
    return true;
  };

  useEffect(() => {
    let alive = true;
    // When a `?category=` filter is present, fetch the pre-filtered list from
    // the backend so slug→id resolution happens server-side and empty results
    // include unknown slugs (rather than the whole catalog).
    const productsUrl = categoryFilter
      ? `${API}/products?category=${encodeURIComponent(categoryFilter)}`
      : `${API}/products`;
    Promise.all([
      axios.get(productsUrl),
      axios.get(`${API}/product-categories`).catch(() => ({ data: { categories: [] } })),
    ]).then(([pRes, cRes]) => {
      if (!alive) return;
      // Sort by list_order (high > medium > low), featured first, then keep
      // the backend's returned order as a tiebreaker.
      const rank = { high: 3, medium: 2, low: 1 };
      let list = [...(pRes.data.products || [])].sort((a, b) => {
        const fa = a.featured ? 1 : 0;
        const fb = b.featured ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return (rank[b.list_order] || 2) - (rank[a.list_order] || 2);
      });
      if (limit > 0) list = list.slice(0, limit);
      setProducts(list);
      setCategories(cRes.data.categories || []);
      // Deep-link support: `?product=<id>` (used by confirmation emails)
      // opens that product's detail modal straight away.
      const wantedId = new URLSearchParams(location.search || "").get("product");
      if (wantedId) {
        const match = list.find((p) => p.product_id === wantedId);
        if (match) setDetail(match);
      }
    }).finally(() => { if (alive) setLoading(false); });
    // Company logo — used as the placeholder for products with no image, or
    // whose image file no longer exists (e.g. lost during a redeploy).
    // Sourced from Site Styles (the single source of truth for branding,
    // synced everywhere) rather than the Header module's own per-instance
    // override, which can go stale independently of Site Styles.
    axios.get(`${API}/site-styles`).then(({ data }) => {
      const raw = data?.companyLogo || "";
      if (alive) setLogoFallback(raw ? resolveImageUrl(raw) : "");
    }).catch(() => {});
    return () => { alive = false; };
  }, [limit, categoryFilter]);

  // Auto-add opted-in categories. When the module loads, any genuinely free
  // product belonging to a category an admin has explicitly flagged
  // (`auto_add_to_basket`) is dropped straight into the basket — e.g. a
  // "Freebies" category. Off by default on every category, so nothing
  // auto-adds unless configured in the category's admin settings. Seed
  // state lives in sessionStorage so a fresh browser session gets the
  // freebies back — but a shopper who explicitly removes one in the same
  // session won't have it re-added.
  useEffect(() => {
    if (loading || products.length === 0 || categories.length === 0) return;
    const SEED_KEY = "slh_freebies_auto_seeded_v1";
    // One-time cleanup — earlier builds stored the seed flag in
    // localStorage which meant returning shoppers never saw the freebies
    // again. Wipe that stale key so this mount can seed fresh.
    try { window.localStorage.removeItem(SEED_KEY); } catch { /* silent */ }
    let seen = [];
    try {
      const raw = window.sessionStorage.getItem(SEED_KEY);
      seen = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(seen)) seen = [];
    } catch { /* sessionStorage blocked — behave as if nothing was seeded */ }
    const freebieCatIds = new Set(
      categories.filter((c) => c?.auto_add_to_basket).map((c) => c.category_id),
    );
    if (freebieCatIds.size === 0) return;
    // Only genuinely free items (every interval price is £0) get
    // auto-added — a paid product sitting in the "Freebies" category by
    // mistake must never auto-seed into a fresh shopper's basket, since it
    // would then count as a real one-off purchase and block their first
    // Monthly/Annual add.
    const freebies = products.filter((p) => (p.category_ids || []).some((cid) => freebieCatIds.has(cid)) && isFreeProduct(p));
    if (freebies.length === 0) return;
    const seenSet = new Set(seen);
    const fresh = freebies.filter((p) => !seenSet.has(p.product_id) && !basket.has(p.product_id));
    if (fresh.length === 0) return;
    fresh.forEach((p) => basket.add(p.product_id, "one_off"));
    const nextSeen = Array.from(new Set([...seen, ...fresh.map((p) => p.product_id)]));
    try { window.sessionStorage.setItem(SEED_KEY, JSON.stringify(nextSeen)); } catch { /* silent */ }
  // basket omitted from deps — this runs once when products arrive; adding
  // basket would re-fire on every mutation and re-add items the user just
  // removed within the same session.
  }, [loading, products, categories]);

  // Auto-add via URL: `?category=<slug>&addToBasket=yes` adds every product
  // in that filtered category straight to the basket — handy for marketing
  // links/promo emails that should land the shopper with their basket
  // pre-filled rather than making them click "Add" on each item. Seeds once
  // per category per session so revisiting the link doesn't keep re-adding
  // items the shopper deliberately removed.
  useEffect(() => {
    if (loading || products.length === 0 || !categoryFilter) return;
    const params = new URLSearchParams(location.search || "");
    if (params.get("addToBasket") !== "yes") return;
    const SEED_KEY = `slh_add_to_basket_seeded_v1_${categoryFilter}`;
    let seeded = false;
    try { seeded = !!window.sessionStorage.getItem(SEED_KEY); } catch { /* sessionStorage blocked */ }
    if (seeded) return;
    const toAdd = products.filter((p) => !basket.has(p.product_id));
    toAdd.forEach((p) => handleAdd(p, defaultInterval(p)));
    try { window.sessionStorage.setItem(SEED_KEY, "1"); } catch { /* silent */ }
  // basket/handleAdd omitted from deps for the same reason as the freebies
  // effect above — this should only fire once when the filtered category's
  // products first arrive.
  }, [loading, products, categoryFilter, location.search]);

  // Resolve which category (if any) the URL slug points at, so we can show
  // its name in the "Filtered by …" chip and offer a Clear link.
  const activeCategory = useMemo(() => {
    if (!categoryFilter) return null;
    return categories.find(
      (c) => c.slug === categoryFilter || c.category_id === categoryFilter,
    ) || null;
  }, [categoryFilter, categories]);

  // Group products by category, ordered by category.display_order. A
  // product with up to 3 category_ids appears in every matching section;
  // products without any category (or pointing only at deleted ones) fall
  // into "Other" and render at the very end so nothing is silently hidden.
  //
  // When a `?category=` filter is active, the server has already scoped
  // `products` down to that one category — skip re-grouping entirely and
  // render it as a single section. Otherwise a product that ALSO belongs
  // to a second category (multi-category) would additionally surface
  // under that second category's heading here, which would make a
  // filtered page look like it isn't actually filtered.
  const grouped = categoryFilter
    ? (products.length
        ? [{
            category_id: activeCategory?.category_id || "",
            name: activeCategory?.name || "",
            show_heading: activeCategory?.show_heading !== false,
            display_order: 0,
            products,
          }]
        : [])
    : (() => {
    const byId = {};
    for (const c of categories) byId[c.category_id] = { ...c, products: [] };
    const other = { category_id: "", name: "Other", display_order: 9999, products: [] };
    for (const p of products) {
      const cids = (p.category_ids || []).filter((cid) => byId[cid]);
      if (cids.length) cids.forEach((cid) => byId[cid].products.push(p));
      else other.products.push(p);
    }
    const sections = Object.values(byId).sort((a, b) => (a.display_order - b.display_order) || a.name.localeCompare(b.name));
    if (other.products.length) sections.push(other);
    // Drop empty categories so the grid isn't peppered with stub headings.
    return sections.filter((s) => s.products.length > 0);
  })();

  // Keep the carousel index valid whenever the category set changes.
  useEffect(() => {
    if (slideIdx > 0 && slideIdx >= grouped.length) setSlideIdx(0);
  }, [grouped.length, slideIdx]);

  const isCarousel = layoutResolved === "carousel" && !categoryFilter;

  return (
    <section className="py-12 px-6" data-testid="products-module">
      <div className="max-w-6xl mx-auto">
        {(title || subtitle) && (
          <div className="mb-8">
            {title && <h2 className={`text-3xl font-serif mb-2 ${moduleTitleAlignCls}`} style={moduleTitleStyle}>{title}</h2>}
            {subtitle && <p className={`text-sm text-muted-foreground w-full ${subtitleAlignCls}`} style={subtitleStyle}>{subtitle}</p>}
          </div>
        )}
        {categoryFilter && activeCategory && (activeCategory.banner_image_url || activeCategory.tagline || activeCategory.description) && (
          <div
            className="mb-8 overflow-hidden border border-border"
            data-testid="products-module-category-banner"
          >
            {activeCategory.banner_image_url && (
              <div
                className="w-full h-48 md:h-56 bg-cover bg-center"
                style={{ backgroundImage: `url(${activeCategory.banner_image_url})` }}
              />
            )}
            {(activeCategory.tagline || activeCategory.description) && (
              <div className="p-6 bg-muted/20 text-center">
                {activeCategory.tagline && (
                  <p className="text-sm font-medium mb-1" data-testid="products-module-category-banner-tagline">
                    {activeCategory.tagline}
                  </p>
                )}
                {activeCategory.description && (
                  <p className="text-sm text-muted-foreground max-w-3xl mx-auto whitespace-pre-line">
                    {activeCategory.description}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {categoryFilter && show_category_line && (
          <div
            className="mb-6 flex items-center justify-center gap-2 text-sm"
            data-testid="products-module-active-filter"
          >
            <span className="text-muted-foreground">Showing category:</span>
            <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
              {activeCategory?.name || categoryFilter}
              <a
                href={location.pathname}
                className="hover:bg-primary/20 rounded-full p-0.5"
                aria-label="Clear category filter"
                data-testid="products-module-clear-filter"
              >
                <X className="h-3 w-3" />
              </a>
            </span>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : products.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12" data-testid="products-module-empty">
            {categoryFilter
              ? <>No products in <strong>{activeCategory?.name || categoryFilter}</strong> yet.</>
              : "No products available yet."}
          </div>
        ) : (
          isCarousel ? (
            <ProductsCarousel
              sections={grouped}
              slideIdx={slideIdx}
              setSlideIdx={setSlideIdx}
              logoFallback={logoFallback}
              basket={basket}
              onOpen={(p) => setDetail(p)}
              onAdd={handleAdd}
              imageScalePct={scale}
              showShortDescription={show_short_description}
              showAddToBasket={show_add_to_basket}
              productNameJustifyCls={productNameJustifyCls}
              showFullDescription={show_full_description}
              sectionTitleStyle={sectionTitleStyle}
              sectionTitleAlignCls={sectionTitleAlignCls}
              timelineStyle={timelineStyleResolved}
              navButtonScalePct={nav_button_scale_pct}
              navButtonTopOffsetPx={nav_button_top_offset_px}
              navFontSizePx={nav_font_size_px}
              navBgColor={nav_bg_color}
              navTextColor={nav_text_color}
            />
          ) : (
          <div className="space-y-12" data-testid="products-module-sections">
            {grouped.map((section) => (
              <div key={section.category_id || "uncategorised"} data-testid={`products-section-${section.category_id || "uncategorised"}`}>
                {section.name && section.show_heading !== false && (
                  <h3
                    className={`text-xl font-serif mb-4 pb-2 border-b border-border/60 ${sectionTitleAlignCls}`}
                    style={sectionTitleStyle}
                    data-testid={`products-section-heading-${section.category_id || "uncategorised"}`}
                  >
                    {section.name}
                  </h3>
                )}
                <div
                  className="border border-border rounded overflow-hidden bg-background"
                  style={table_border_color ? { borderColor: table_border_color } : undefined}
                >
                  <table className="w-full text-sm" data-testid="products-module-table">
                    <thead className="bg-muted/40 text-left">
                      <tr>
                        <th className="p-3 w-16 sm:w-20"></th>
                        <th className="p-3"></th>
                        <th className="p-3 w-32 text-right hidden sm:table-cell">Price</th>
                        <th className="p-3 w-40 text-right hidden sm:table-cell"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.products.map((p) => (
                        <ProductRow
                          key={p.product_id}
                          product={p}
                          logoFallback={logoFallback}
                          inBasket={basket.has(p.product_id)}
                          basketInterval={basket.items.find((i) => i.product_id === p.product_id)?.billing_interval}
                          showShortDescription={show_short_description}
                          showDescriptionPreview={show_description_preview}
                          showFullDescription={show_full_description}
                          showSupplier={show_supplier}
                          showAddToBasket={show_add_to_basket}
                          imageScalePct={scale}
                          onOpen={() => setDetail(p)}
                          onAdd={(interval) => handleAdd(p, interval)}
                          onRemove={() => { basket.remove(p.product_id); toast(`Removed "${p.name}"`); }}
                          productNameJustifyCls={productNameJustifyCls}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          )
        )}
      </div>
      {detail && (
        <ProductDetailModal
          product={detail}
          logoFallback={logoFallback}
          inBasket={basket.has(detail.product_id)}
          basketInterval={basket.items.find((i) => i.product_id === detail.product_id)?.billing_interval}
          showAddToBasket={show_add_to_basket}
          onClose={() => setDetail(null)}
          onAdd={(interval) => {
            if (!handleAdd(detail, interval)) return;
            // Close the details modal once the item is queued — buyers
            // expect the dialog to dismiss so they can keep browsing.
            setDetail(null);
          }}
          onRemove={() => { basket.remove(detail.product_id); toast(`Removed "${detail.name}"`); }}
        />
      )}
    </section>
  );
}

function ProductsCarousel({ sections, slideIdx, setSlideIdx, logoFallback, basket, onOpen, onAdd, imageScalePct = 100, showShortDescription = true, showAddToBasket = true, productNameJustifyCls = "justify-start", showFullDescription = false, sectionTitleStyle = {}, sectionTitleAlignCls = "text-center", timelineStyle = "slim", navButtonScalePct = 100, navButtonTopOffsetPx = 0, navFontSizePx = 0, navBgColor = "", navTextColor = "" }) {
  const navigate = useNavigate();
  const total = sections.length;
  const carouselRef = useRef(null);
  // Per-slide refs so we can size the track to the active slide only —
  // flexbox otherwise stretches every child to the tallest sibling and
  // leaves huge empty space under shorter categories.
  const slideRefs = useRef([]);
  const [trackHeight, setTrackHeight] = useState(null);
  // Viewport-relative y-position (px from top of the browser window) at
  // which the Prev/Next buttons sit. The buttons are `position: fixed` to
  // the page's left/right edges, and this `top` value is recomputed on
  // scroll/resize so they stay vertically aligned with the active slide's
  // category heading (`<h3>`). Nulled out when the heading is off-screen
  // so we don't leave floating buttons hovering over unrelated sections.
  const [navViewportTop, setNavViewportTop] = useState(null);
  // Swipe / drag / trackpad handling. One coherent gesture state so the
  // same code path handles a phone flick, a mouse drag, and a two-finger
  // trackpad swipe. Vertical movement is respected — if the user is
  // scrolling the page, we DON'T hijack their gesture.
  const gesture = useRef({ active: false, x0: 0, y0: 0, dx: 0, dy: 0, axis: null });
  const wheelBudget = useRef(0);
  const wheelCooldown = useRef(0);
  const clamped = total === 0 ? 0 : Math.min(Math.max(slideIdx, 0), total - 1);
  // Size the track to the active slide instead of the tallest slide.
  // Recompute whenever the active index changes, whenever the viewport
  // resizes (card layout reflows), and whenever the slide's own contents
  // change (image loads, filters, etc.). ResizeObserver on the active
  // node covers the "images loaded late" case for free.
  useEffect(() => {
    const el = slideRefs.current[clamped];
    if (!el) return undefined;
    const sync = () => {
      setTrackHeight(el.offsetHeight);
    };
    sync();
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(sync);
      ro.observe(el);
    }
    window.addEventListener("resize", sync);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [clamped, total]);

  // Track the vertical target for the fixed-edge Prev/Next buttons as the
  // user scrolls. Positioning is measured from the top of the active
  // slide's first product card, minus `navButtonTopOffsetPx` — positive
  // offset lifts the buttons above the card, negative offset dips them
  // into the card area. When there's no card yet (empty category) we fall
  // back to the section heading centre so the pill still lands somewhere
  // sensible. Hidden when the target is scrolled off-screen so we don't
  // leave floaters over unrelated sections.
  useEffect(() => {
    const compute = () => {
      const el = slideRefs.current[clamped];
      if (!el) { setNavViewportTop(null); return; }
      const grid = el.querySelector(`[data-testid^="products-carousel-grid-"]`);
      const firstCard = grid && grid.children[0];
      let y = null;
      if (firstCard) {
        const r = firstCard.getBoundingClientRect();
        y = r.top - Number(navButtonTopOffsetPx || 0);
      } else {
        const heading = el.querySelector(`h3[data-testid^="products-carousel-heading-"]`);
        if (heading) {
          const r = heading.getBoundingClientRect();
          y = r.top + r.height / 2;
        }
      }
      if (y == null) { setNavViewportTop(null); return; }
      const inView = y > 60 && y < window.innerHeight - 40;
      setNavViewportTop(inView ? y : null);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [clamped, total, navButtonTopOffsetPx]);

  if (total === 0) return null;
  const isLast = clamped === total - 1;
  const prev = () => setSlideIdx((i) => (i - 1 + total) % total);
  const next = () => {
    // On the final category slide, "next" advances into checkout — but
    // only if the basket has items. An empty basket just wraps back to
    // the first category so the buyer isn't dumped on an empty checkout.
    if (isLast) {
      if (basket.count > 0) navigate("/checkout");
      else setSlideIdx(0);
      return;
    }
    setSlideIdx((i) => Math.min(i + 1, total - 1));
  };

  // Swipe / drag / trackpad handling. Constants declared inline; refs are
  // hoisted above the early-return so hook order stays stable.
  const SWIPE_PX = 50;             // horizontal distance to trigger a slide change
  const WHEEL_PX = 60;             // trackpad deltaX budget before triggering

  const startGesture = (x, y) => {
    gesture.current = { active: true, x0: x, y0: y, dx: 0, dy: 0, axis: null };
  };
  const moveGesture = (x, y) => {
    if (!gesture.current.active) return;
    gesture.current.dx = x - gesture.current.x0;
    gesture.current.dy = y - gesture.current.y0;
    if (!gesture.current.axis) {
      // Lock the axis once one component clearly dominates (>10 px). Prevents
      // us from stealing a vertical page-scroll after a small horizontal jiggle.
      if (Math.abs(gesture.current.dx) > 10 || Math.abs(gesture.current.dy) > 10) {
        gesture.current.axis = Math.abs(gesture.current.dx) > Math.abs(gesture.current.dy) ? "x" : "y";
      }
    }
  };
  const endGesture = () => {
    if (!gesture.current.active) return;
    const { dx, axis } = gesture.current;
    gesture.current.active = false;
    if (axis === "x" && Math.abs(dx) > SWIPE_PX) {
      if (dx < 0) next(); else prev();
    }
  };

  const onTouchStart = (e) => {
    const t = e.touches[0]; if (!t) return;
    startGesture(t.clientX, t.clientY);
  };
  const onTouchMove = (e) => {
    const t = e.touches[0]; if (!t) return;
    moveGesture(t.clientX, t.clientY);
  };
  const onTouchEnd = () => endGesture();

  const onPointerDown = (e) => {
    // Mouse drag only — touch is handled by touch events which give us the
    // "click vs drag" heuristic for free. Ignore anything that isn't the
    // primary button on a real mouse.
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    startGesture(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!gesture.current.active || e.pointerType !== "mouse") return;
    moveGesture(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    if (e.pointerType !== "mouse") return;
    endGesture();
  };

  const onWheel = (e) => {
    // Trackpad two-finger horizontal swipe. Only intercept when the user
    // is clearly moving horizontally — vertical wheel = page scroll.
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    if (Math.abs(e.deltaX) < 4) return;
    const nowTs = e.timeStamp || Date.now();
    if (nowTs - wheelCooldown.current < 400) return;   // one slide per burst
    wheelBudget.current += e.deltaX;
    if (Math.abs(wheelBudget.current) < WHEEL_PX) return;
    if (wheelBudget.current > 0) next(); else prev();
    wheelBudget.current = 0;
    wheelCooldown.current = nowTs;
  };

  return (
    <div ref={carouselRef} className="relative" data-testid="products-module-carousel">
      {/* Category racetrack — single-row compact timeline.
          Design goals:
            • collapses ~80px stepper into a ~44px slim rail
            • one visible label at a time (the active step) inside a pill
            • coloured progress fill grows left→right, easing on change
            • Checkout sits at the finish line as a glowing terminal
            • horizontal-scroll on overflow so long category lists still fit,
              with the active pill auto-centred via scrollIntoView. */}
      <CategoryTimeline
        sections={sections}
        activeIndex={clamped}
        onSelect={setSlideIdx}
        basketCount={basket.count}
        variant={timelineStyle}
      />

      {/* Slides viewport — horizontal track that smoothly translates when
          prev/next (or a timeline node) is clicked. Prev/Next buttons are
          rendered OUTSIDE this container (absolute-anchored to the outer
          carousel container edges) so the CSS `transform` on the flex
          track doesn't trap them and misalign the left/right pinning.
          Touch / mouse-drag / trackpad-swipe are all wired here so mobile
          users get a native feel. `touch-pan-y` lets vertical page scroll
          pass through untouched. */}
      <div className="relative">

        <div
          className="overflow-hidden touch-pan-y select-none"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          data-testid="products-carousel-viewport"
        >
          <div
            className="flex items-start will-change-transform"
            style={{
              width: `${total * 100}%`,
              transform: `translateX(-${(clamped * 100) / total}%)`,
              transition: "transform 500ms cubic-bezier(0.4, 0, 0.2, 1), height 300ms ease-out",
              height: trackHeight != null ? `${trackHeight}px` : undefined,
            }}
            data-testid="products-carousel-track"
          >
            {sections.map((s, i) => (
              <div
                key={s.category_id || `s-${s.name}`}
                ref={(node) => { slideRefs.current[i] = node; }}
                className="shrink-0 px-4 md:px-10"
                style={{ width: `${100 / total}%` }}
                data-testid={`products-carousel-slide-${s.category_id || "uncategorised"}`}
              >
                {(s.name || s.tagline) && (
                  <div className={`mb-3 ${sectionTitleAlignCls}`}>
                    {s.name && s.show_heading !== false && (
                      <h3
                        className="text-2xl font-serif"
                        style={sectionTitleStyle}
                        data-testid={`products-carousel-heading-${s.category_id || "uncategorised"}`}
                      >
                        {s.name}
                      </h3>
                    )}
                    {s.tagline && (
                      <p className="text-sm text-muted-foreground mt-1">{s.tagline}</p>
                    )}
                  </div>
                )}

                {s.banner_image_url && (
                  <div
                    className="w-full h-40 md:h-48 bg-cover bg-center mb-6 border border-border"
                    style={{ backgroundImage: `url(${s.banner_image_url})` }}
                  />
                )}

                {/* Horizontal cards → shorter rows → 1 col on mobile, 2 on
                    md+. Cuts card height roughly in half vs. stacked cards.
                    Wrapped in a relative container so the Prev/Next buttons
                    can absolute-anchor to the vertical middle of the first
                    card (measured via ref) — sits at the natural
                    "left / right of the row" position users expect. */}
                <div className="relative">
                  {/* Nav buttons rendered outside the transformed slide
                      track (see below) — position:fixed inside a
                      transformed ancestor fixes to the ancestor, not the
                      viewport, so pinning to the page edges requires
                      escaping the flex track. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`products-carousel-grid-${s.category_id || "uncategorised"}`}>
                  {s.products.map((p) => (
                    <ProductCard
                      key={p.product_id}
                      product={p}
                      logoFallback={logoFallback}
                      inBasket={basket.has(p.product_id)}
                      basketInterval={basket.items.find((i) => i.product_id === p.product_id)?.billing_interval}
                      imageScalePct={imageScalePct}
                      showShortDescription={showShortDescription}
                      showAddToBasket={showAddToBasket}
                      productNameJustifyCls={productNameJustifyCls}
                      showFullDescription={showFullDescription}
                      horizontal
                      onOpen={() => onOpen(p)}
                      onAdd={(interval) => onAdd(p, interval)}
                      onRemove={() => { basket.remove(p.product_id); toast(`Removed "${p.name}"`); }}
                    />
                  ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Nav buttons — pinned to the browser's left/right edges via
          `position: fixed`, vertically tracked to the top of the active
          slide's first product card (see `navViewportTop` effect above).
          Every visual knob (size %, top-offset px, label font size, bg
          and text colour) is admin-configurable on the module instance so
          the same carousel can theme itself per page. */}
      {total > 1 && (() => {
        const s = sections[clamped] || {};
        const sName = (s.name || "").toLowerCase().trim();
        const sSlug = (s.slug || "").toLowerCase().trim();
        const isFreebiesSlide = sName === "freebies" || sSlug === "freebies";
        const navScale = Math.max(30, Math.min(300, Number(navButtonScalePct) || 100)) / 100;
        if (navViewportTop == null) return null;
        const commonStyle = { position: "fixed", top: `${navViewportTop}px` };
        // Colour overrides only take effect when the admin has explicitly
        // picked a hex — empty string falls back to the Tailwind classes
        // driven by Site Styles, preserving the current look for anyone
        // who hasn't customised.
        const colorStyle = {};
        if (navBgColor) colorStyle.backgroundColor = navBgColor;
        if (navTextColor) colorStyle.color = navTextColor;
        const fontStyle = Number(navFontSizePx) > 0
          ? { fontSize: `${Number(navFontSizePx)}px` }
          : {};
        return (
          <div data-testid="products-carousel-nav-row" className="contents">
            {!isFreebiesSlide && (
              <button
                type="button"
                onClick={prev}
                aria-label="Previous category"
                data-testid="products-carousel-prev"
                className={`rounded-full border-2 border-primary-foreground/20 shadow-xl hover:shadow-2xl hover:brightness-110 h-12 pl-3 pr-4 flex items-center gap-1.5 transition-all font-semibold text-base z-40 ${
                  navBgColor ? "" : "bg-primary"
                } ${navTextColor ? "" : "text-primary-foreground"}`}
                style={{
                  ...commonStyle,
                  ...colorStyle,
                  ...fontStyle,
                  left: "8px",
                  transform: `translateY(-50%) scale(${navScale})`,
                  transformOrigin: "left center",
                }}
              >
                <ChevronLeft className="h-5 w-5 shrink-0" />
                <span>Previous</span>
              </button>
            )}
            <button
              type="button"
              onClick={next}
              aria-label={isLast ? "Go to checkout" : "Next category"}
              title={isLast ? "Go to checkout" : "Next category"}
              data-testid={isLast ? "products-carousel-next-checkout" : "products-carousel-next"}
              className={`rounded-full border-2 shadow-xl hover:shadow-2xl h-12 pl-4 pr-3 flex items-center gap-1.5 transition-all font-semibold text-base z-40 ${
                isLast
                  ? "bg-emerald-600 text-white border-white/25 hover:brightness-110"
                  : `border-primary-foreground/20 hover:brightness-110 ${navBgColor ? "" : "bg-primary"} ${navTextColor ? "" : "text-primary-foreground"}`
              }`}
              style={{
                ...commonStyle,
                // Checkout state keeps its emerald branding; category "Next"
                // honours the admin's colour picks.
                ...(isLast ? {} : colorStyle),
                ...fontStyle,
                right: "8px",
                transform: `translateY(-50%) scale(${navScale})`,
                transformOrigin: "right center",
              }}
            >
              {isLast ? (
                <>
                  <span>Checkout</span>
                  <CreditCard className="h-5 w-5 shrink-0" />
                </>
              ) : (
                <>
                  <span>Next</span>
                  <ChevronRight className="h-5 w-5 shrink-0" />
                </>
              )}
            </button>
          </div>
        );
      })()}

      {/* Nav arrows are rendered inline within each active slide's card
          grid, aligned vertically with the middle of the first product
          card (measured via ref). */}

      {/* Dot pagination — click to jump directly */}
      {total > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2" data-testid="products-carousel-dots">
          {sections.map((s, i) => (
            <button
              key={s.category_id || `idx-${i}`}
              type="button"
              onClick={() => setSlideIdx(i)}
              aria-label={`Show ${s.name}`}
              data-testid={`products-carousel-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === clamped ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, logoFallback, inBasket, basketInterval, onOpen, onAdd, onRemove, imageScalePct = 100, showShortDescription = true, showAddToBasket = true, horizontal = false, productNameJustifyCls = "justify-start", showFullDescription = false }) {
  // Falls back to the site logo both when there's no image set AND when the
  // image file 404s (broken/missing upload) — `imgFailed` catches the latter
  // since that can only be detected once the browser actually tries to load it.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [product.image_url]);
  const usingLogo = !product.image_url || imgFailed;
  const src = usingLogo ? logoFallback : resolveImageUrl(product.image_url);
  const isLogo = usingLogo && Boolean(logoFallback);
  const fitClass = product.image_fit === "cover" ? "object-cover" : "object-contain";
  const effectiveFit = isLogo ? "object-contain" : fitClass;
  const scale = Math.max(10, Math.min(100, Number(imageScalePct) || 100));
  const [billingInterval, setBillingInterval] = useState(() => defaultInterval(product));
  // Once the item is in the basket, reflect whichever interval it was
  // actually added with (e.g. auto-seeded freebies, or a previous session)
  // instead of leaving the toggle on its default.
  useEffect(() => {
    if (inBasket && basketInterval && basketInterval !== billingInterval) setBillingInterval(basketInterval);
  }, [inBasket, basketInterval]);
  // Horizontal cards use a wider-than-tall thumbnail (square-ish) on the
  // left so the total card height matches the text block on the right —
  // dramatically shorter than the stacked variant.
  const imgWrapStyle = horizontal
    ? { width: `${(140 * scale) / 100}px`, minWidth: `${(140 * scale) / 100}px`, height: "100%" }
    : { height: `${(160 * scale) / 100}px` };
  return (
    <div
      className={`relative border rounded overflow-hidden cursor-pointer transition-all hover:shadow-md ${horizontal ? "flex flex-row items-stretch" : "flex flex-col"} ${inBasket ? "border-emerald-300 bg-emerald-50/60" : "border-border bg-background"}`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      title="Click for more info"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Info indicator — decorative; the whole card is clickable. */}
      <span
        aria-hidden="true"
        className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-background/95 backdrop-blur border border-border shadow-sm flex items-center justify-center text-primary opacity-80 group-hover:opacity-100 transition-opacity"
        data-testid={`product-card-info-${product.product_id}`}
      >
        <InfoIcon className="h-4 w-4" />
      </span>
      <div
        className={`relative bg-muted/40 flex items-center justify-center overflow-hidden ${horizontal ? "shrink-0" : ""}`}
        style={imgWrapStyle}
      >
        {src ? (
          <img
            src={src}
            alt={product.name}
            className={`${isLogo ? "h-2/3 w-2/3 opacity-70" : "w-full h-full"} ${effectiveFit}`}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span className="text-xs text-muted-foreground">No image</span>
        )}
        {product.badge_text && (
          <ProductBadge
            text={product.badge_text}
            position={product.badge_position}
            bgColor={product.badge_bg_color}
            textColor={product.badge_text_color}
            fontFamily={product.badge_font_family}
            fontSize={product.badge_font_size}
            rotation={product.badge_rotation}
            testId={`product-card-badge-${product.product_id}`}
          />
        )}
      </div>
      <div className={`p-3 flex flex-col gap-2 flex-1 ${horizontal ? "min-w-0" : ""}`}>
        <div className={`font-semibold text-sm leading-snug flex items-center flex-wrap gap-2 pr-8 ${productNameJustifyCls}`}>
          <span className={horizontal ? "line-clamp-1" : "line-clamp-2"}>{product.name}</span>
          {inBasket && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              <Check className="h-3 w-3" /> In basket
            </span>
          )}
        </div>
        {showShortDescription && product.short_description && (
          <p className={`text-xs text-muted-foreground ${horizontal ? "line-clamp-2" : "line-clamp-3"}`}>{product.short_description}</p>
        )}
        {showFullDescription && product.description && (
          <div
            className="text-xs text-muted-foreground [&_p]:m-0 [&_p]:leading-snug"
            data-testid={`product-card-desc-full-${product.product_id}`}
          >
            <RichHtml html={product.description} />
          </div>
        )}
        <BillingIntervalToggle product={product} value={billingInterval} onChange={setBillingInterval} testIdPrefix="product-card-interval" />
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <div className="font-serif text-base whitespace-nowrap" data-testid={`product-card-price-${product.product_id}`}>
            {formatIntervalPrice(product, billingInterval)}
          </div>
          {inBasket ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded transition-colors border border-red-200 text-red-600 hover:bg-red-50"
              data-testid={`product-card-remove-${product.product_id}`}
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          ) : showAddToBasket && product.allow_add_to_basket !== false ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAdd(billingInterval); }}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded transition-colors bg-primary text-primary-foreground hover:opacity-90"
              data-testid={`product-card-add-${product.product_id}`}
            >
              <ShoppingCart className="h-3 w-3" /> Add
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProductRow({
  product,
  logoFallback,
  inBasket,
  basketInterval,
  showShortDescription = true,
  showDescriptionPreview = false,
  showFullDescription = false,
  showSupplier = false,
  showAddToBasket = true,
  imageScalePct = 100,
  productNameJustifyCls = "justify-start",
  onOpen,
  onAdd,
  onRemove,
}) {
  // Falls back to the site logo both when there's no image set AND when the
  // image file 404s (broken/missing upload) — `imgFailed` catches the latter
  // since that can only be detected once the browser actually tries to load it.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [product.image_url]);
  const usingLogo = !product.image_url || imgFailed;
  const src = usingLogo ? logoFallback : resolveImageUrl(product.image_url);
  const isLogo = usingLogo && Boolean(logoFallback);
  const fitClass = product.image_fit === "cover" ? "object-cover" : "object-contain";
  const effectiveFit = isLogo ? "object-contain" : fitClass;
  const [billingInterval, setBillingInterval] = useState(() => defaultInterval(product));
  useEffect(() => {
    if (inBasket && basketInterval && basketInterval !== billingInterval) setBillingInterval(basketInterval);
  }, [inBasket, basketInterval]);

  // Rendered on mobile inline with the product name so nothing gets clipped
  // when the standalone Price / Action columns collapse.
  const priceBlock = (
    <div className="whitespace-nowrap">
      <div className="font-serif text-lg" data-testid={`product-price-${product.product_id}`}>
        {formatIntervalPrice(product, billingInterval)}
      </div>
      {billingInterval === "one_off" && Number(product.was_price || 0) > Number(product.price || 0) && (
        <div
          className="text-xs text-muted-foreground line-through"
          data-testid={`product-was-price-${product.product_id}`}
        >
          {symbolFor(product.currency)}{Number(product.was_price).toFixed(2)}
        </div>
      )}
      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
        <BillingIntervalToggle product={product} value={billingInterval} onChange={setBillingInterval} testIdPrefix="product-row-interval" />
      </div>
    </div>
  );
  const ctaButton = !showAddToBasket ? null : inBasket ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded transition-colors border border-red-200 text-red-600 hover:bg-red-50"
      data-testid={`product-remove-${product.product_id}`}
      title="Remove from basket"
    >
      <Trash2 className="h-3 w-3" />
      Remove
    </button>
  ) : product.allow_add_to_basket === false ? null : (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onAdd(billingInterval); }}
      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded transition-colors bg-primary text-primary-foreground hover:opacity-90"
      data-testid={`product-add-${product.product_id}`}
    >
      <ShoppingCart className="h-3 w-3" />
      Add
    </button>
  );

  return (
    <tr
      className={`border-t border-border cursor-pointer transition-colors ${inBasket ? "bg-emerald-50/60 hover:bg-emerald-50" : "hover:bg-muted/30"}`}
      onClick={onOpen}
      data-testid={`product-row-${product.product_id}`}
      data-in-basket={inBasket ? "true" : "false"}
    >
      <td className="p-3 align-top">
        <div
          className="relative bg-muted/40 rounded overflow-hidden flex items-center justify-center"
          style={{
            height: `${(56 * Math.max(10, Math.min(100, Number(imageScalePct) || 100))) / 100}px`,
            width: `${(56 * Math.max(10, Math.min(100, Number(imageScalePct) || 100))) / 100}px`,
          }}
        >
          {src ? (
            <img
              src={src}
              alt={product.name}
              className={`${isLogo ? "h-2/3 w-2/3 opacity-70" : "w-full h-full"} ${effectiveFit}`}
              data-testid={isLogo ? `product-logo-fallback-${product.product_id}` : undefined}
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className="text-[10px] text-muted-foreground">No image</span>
          )}
          {product.badge_text && (
            <ProductBadge
              text={product.badge_text}
              position={product.badge_position}
              bgColor={product.badge_bg_color}
              textColor={product.badge_text_color}
              fontFamily={product.badge_font_family}
              fontSize={product.badge_font_size}
              rotation={product.badge_rotation}
              testId={`product-row-badge-${product.product_id}`}
            />
          )}
        </div>
      </td>
      <td className="p-3 align-top">
        <div className={`font-semibold text-base leading-snug flex items-center flex-wrap gap-2 ${productNameJustifyCls}`}>
          <span>{product.name}</span>
          {inBasket && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"
              data-testid={`product-in-basket-badge-${product.product_id}`}
            >
              <Check className="h-3 w-3" /> In basket
            </span>
          )}
        </div>
        {showShortDescription && product.short_description && (
          <p
            className="text-xs text-muted-foreground mt-1 line-clamp-2 max-w-xl"
            data-testid={`product-short-desc-${product.product_id}`}
          >
            {product.short_description}
          </p>
        )}
        {showFullDescription && product.description ? (
          <div
            className="text-xs text-muted-foreground mt-1 max-w-xl [&_p]:m-0 [&_p]:leading-snug"
            data-testid={`product-desc-full-${product.product_id}`}
          >
            <RichHtml html={product.description} />
          </div>
        ) : showDescriptionPreview && product.description && (
          <div
            className="text-xs text-muted-foreground mt-1 max-w-xl [&_p]:m-0 [&_p]:leading-snug"
            data-testid={`product-desc-preview-${product.product_id}`}
          >
            <RichHtml
              className="line-clamp-5"
              html={product.description}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              className="text-primary hover:underline text-xs font-medium mt-0.5"
              data-testid={`product-desc-more-${product.product_id}`}
            >
              more…
            </button>
          </div>
        )}
        {showSupplier && product.supplier_name && (
          <p
            className="text-[11px] text-muted-foreground mt-1 max-w-xl"
            data-testid={`product-supplier-${product.product_id}`}
          >
            Supplied by <span className="font-medium">{product.supplier_name}</span>
          </p>
        )}
        {/* Mobile-only price + CTA — visible below sm; on sm+ these live in
            their own dedicated <td>s so the layout aligns as a table. */}
        <div className="mt-3 flex items-center justify-between gap-3 sm:hidden">
          {priceBlock}
          {ctaButton}
        </div>
      </td>
      <td className="p-3 text-right whitespace-nowrap hidden sm:table-cell align-top">
        {priceBlock}
      </td>
      <td className="p-3 text-right hidden sm:table-cell align-top">
        {ctaButton}
      </td>
    </tr>
  );
}

function ProductDetailModal({ product, logoFallback, inBasket, basketInterval, showAddToBasket = true, onClose, onAdd, onRemove }) {
  useLockBodyScroll(true);
  // Falls back to the site logo both when there's no image set AND when the
  // image file 404s (broken/missing upload) — `imgFailed` catches the latter
  // since that can only be detected once the browser actually tries to load it.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [product.image_url]);
  const usingLogo = !product.image_url || imgFailed;
  const src = usingLogo ? logoFallback : resolveImageUrl(product.image_url);
  const fitClass = usingLogo ? "object-contain" : (product.image_fit === "cover" ? "object-cover" : "object-contain");
  const [showBecomeSupplier, setShowBecomeSupplier] = useState(false);
  const [billingInterval, setBillingInterval] = useState(() => defaultInterval(product));
  useEffect(() => {
    if (inBasket && basketInterval && basketInterval !== billingInterval) setBillingInterval(basketInterval);
  }, [inBasket, basketInterval]);
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid={`product-detail-${product.product_id}`}
    >
      <div
        className="bg-background max-w-2xl w-full max-h-[90vh] rounded shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
          <h3 className="font-serif text-xl" data-testid="product-detail-name">{product.name}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
            aria-label="Close"
            data-testid="product-detail-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {product.job_description && (
          <div className="flex items-center justify-end gap-2 px-5 py-1.5 bg-primary/5 border-b border-primary/10 shrink-0">
            <span className="text-xs text-muted-foreground/70">Interested in supplying this?</span>
            <button
              type="button"
              onClick={() => setShowBecomeSupplier(true)}
              className="text-xs font-medium px-2 py-1 rounded border border-primary/50 text-primary hover:bg-primary/10 transition-colors whitespace-nowrap"
              data-testid="product-detail-become-supplier-top"
            >
              Become a Supplier
            </button>
          </div>
        )}
        {/* Scrollable body only — the footer CTA bar below is kept outside
            this scroll area (shrink-0, not overflow-y-auto) so "Add to
            basket" stays on-screen no matter how long the description or
            how tall the image is, instead of scrolling out of view. */}
        <div className="overflow-y-auto">
          {src && (
            <div className="relative bg-muted/40 flex items-center justify-center" style={{ height: 320 }}>
              <img src={src} alt={product.name} className={`max-h-full max-w-full ${fitClass}`} onError={() => setImgFailed(true)} />
              {product.badge_text && (
                <ProductBadge
                  text={product.badge_text}
                  position={product.badge_position}
                  bgColor={product.badge_bg_color}
                  textColor={product.badge_text_color}
                  fontFamily={product.badge_font_family}
                  fontSize={product.badge_font_size}
                  rotation={product.badge_rotation}
                  testId={`product-detail-badge-${product.product_id}`}
                />
              )}
            </div>
          )}
          <div className="p-5 space-y-4">
            {product.short_description && (
              <p className="text-sm text-muted-foreground" data-testid="product-detail-short-desc">
                {product.short_description}
              </p>
            )}
            {product.supplier_name && (
              <p className="text-xs text-muted-foreground" data-testid="product-detail-supplier">
                Supplied by <span className="font-medium">{product.supplier_name}</span>
              </p>
            )}
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-serif text-2xl" data-testid="product-detail-price">
                {formatIntervalPrice(product, billingInterval)}
              </span>
              {billingInterval === "one_off" && Number(product.was_price || 0) > Number(product.price || 0) && (
                <span className="text-sm text-muted-foreground line-through">
                  {symbolFor(product.currency)}{Number(product.was_price).toFixed(2)}
                </span>
              )}
              <BillingIntervalToggle product={product} value={billingInterval} onChange={setBillingInterval} testIdPrefix="product-detail-interval" />
            </div>
            {product.description && (
              <RichHtml
                className="prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline"
                data-testid="product-detail-description"
                html={product.description}
              />
            )}
          </div>
        </div>
        <div className="border-t px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          {product.job_description ? (
            <button
              type="button"
              onClick={() => setShowBecomeSupplier(true)}
              className="text-sm font-medium px-4 py-2 rounded border border-primary text-primary hover:bg-primary/5 transition-colors"
              data-testid="product-detail-become-supplier"
            >
              Become a Supplier
            </button>
          ) : <span />}
          {!showAddToBasket ? null : inBasket ? (
            <button
              type="button"
              onClick={onRemove}
              className="text-sm font-medium px-4 py-2 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
              data-testid="product-detail-remove"
            >
              Remove from basket
            </button>
          ) : product.allow_add_to_basket === false ? null : (
            <button
              type="button"
              onClick={() => onAdd(billingInterval)}
              className="text-sm font-medium px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90 transition-colors inline-flex items-center gap-2"
              data-testid="product-detail-add"
            >
              <ShoppingCart className="h-4 w-4" /> Add to basket
            </button>
          )}
        </div>
      </div>
      {showBecomeSupplier && (
        <BecomeSupplierModal product={product} onClose={() => setShowBecomeSupplier(false)} />
      )}
    </div>
  );
}


/**
 * CategoryTimeline — three visual densities via `variant`:
 *   • "slim"      → compact ~44px racetrack (default; ships since Feb 2026)
 *   • "chevrons"  → legacy numbered stepper (one node + label per category)
 *   • "hidden"    → renders nothing (useful when the page only has 1 category
 *                    or the admin wants a cleaner hero-only look)
 *
 * Slim-rail anatomy (left → right):
 *   • base rail (bg-muted)      — the neutral track
 *   • progress fill (bg-primary) — grows to (activeIndex+1)/(sections.length+1)
 *   • dots                       — one tiny circle per category, positioned
 *                                   at (i+0.5)/(n+1) along the rail
 *   • active pill                — floats over the active dot, shows the
 *                                   step counter and category name
 *   • checkout flag              — terminal node at 100%, glows on basket>0
 */
function CategoryTimeline({ sections, activeIndex, onSelect, basketCount, variant = "slim" }) {
  const total = sections.length;
  const activeName = sections[activeIndex]?.name || "";

  // Auto-centre the active pill horizontally on overflow so long category
  // lists stay usable on narrow screens without manual scrolling.
  const railRef = useRef(null);
  useEffect(() => {
    if (variant === "hidden") return;
    const rail = railRef.current;
    if (!rail) return;
    const pill = rail.querySelector('[data-active-pill="true"]');
    if (pill && typeof pill.scrollIntoView === "function") {
      pill.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeIndex, variant]);

  if (variant === "hidden") {
    // Screen readers still deserve to know which step is active even when
    // the visual timeline is suppressed.
    return (
      <span className="sr-only" data-testid="products-carousel-timeline-hidden">
        Step {activeIndex + 1} of {total}: {activeName}
      </span>
    );
  }

  if (variant === "chevrons") {
    // Legacy numbered stepper — kept for admins who prefer the classic look.
    return (
      <ol
        className="mb-8 flex items-center justify-center flex-wrap gap-y-3 gap-x-1"
        data-testid="products-carousel-timeline"
        data-timeline-variant="chevrons"
        aria-label="Category progress"
      >
        {sections.map((s, i) => {
          const isDone = i < activeIndex;
          const isActive = i === activeIndex;
          const nodeCls = isActive
            ? "bg-primary text-primary-foreground border-primary shadow"
            : isDone
              ? "bg-primary/15 text-primary border-primary/40"
              : "bg-background text-muted-foreground border-border";
          return (
            <li key={s.category_id || `t-${i}`} className="flex items-center">
              <button
                type="button"
                onClick={() => onSelect(i)}
                data-testid={`products-carousel-timeline-node-${i}`}
                aria-current={isActive ? "step" : undefined}
                className="flex flex-col items-center gap-1 group"
                title={s.name}
              >
                <span
                  className={`h-7 w-7 rounded-full border-2 flex items-center justify-center text-[11px] font-semibold transition-all ${nodeCls} group-hover:scale-105`}
                >
                  {isDone ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wide max-w-[80px] leading-tight text-center line-clamp-2 ${isActive ? "text-primary font-semibold" : "text-muted-foreground"}`}
                >
                  {s.name}
                </span>
              </button>
              <span
                aria-hidden="true"
                className={`w-6 sm:w-10 md:w-14 h-0.5 mx-1 mb-6 transition-colors ${i < activeIndex ? "bg-primary/40" : "bg-border"}`}
              />
            </li>
          );
        })}
        <li className="flex items-center">
          {basketCount > 0 ? (
            <Link
              to="/checkout"
              data-testid="products-carousel-timeline-checkout"
              className="flex flex-col items-center gap-1 group"
              title={`Checkout (${basketCount} items)`}
            >
              <span className="h-8 w-8 rounded-full border-2 flex items-center justify-center transition-all group-hover:scale-105 bg-primary text-primary-foreground border-primary shadow">
                <CreditCard className="h-4 w-4" />
              </span>
              <span className="text-[10px] uppercase tracking-wide max-w-[80px] leading-tight text-center font-semibold text-primary">
                Checkout ({basketCount})
              </span>
            </Link>
          ) : (
            // Empty basket — render as a disabled, greyed-out marker.
            // Keeps the visual step in place so the timeline layout is
            // stable, but blocks navigation and swaps in a helpful tooltip.
            <span
              data-testid="products-carousel-timeline-checkout"
              data-disabled="true"
              aria-disabled="true"
              title="Add an item to your basket to continue to checkout"
              className="flex flex-col items-center gap-1 cursor-not-allowed opacity-50 select-none"
            >
              <span className="h-8 w-8 rounded-full border-2 flex items-center justify-center bg-muted text-muted-foreground border-dashed border-muted-foreground/30">
                <CreditCard className="h-4 w-4" />
              </span>
              <span className="text-[10px] uppercase tracking-wide max-w-[80px] leading-tight text-center font-semibold text-muted-foreground">
                Checkout
              </span>
            </span>
          )}
        </li>
      </ol>
    );
  }

  // Default: "slim" racetrack — compact single-row.
  const progress = total === 0 ? 0 : Math.min(1, (activeIndex + 1) / (total + 1));
  return (
    <div
      className="mb-8 relative"
      data-testid="products-carousel-timeline"
      data-timeline-variant="slim"
      aria-label="Category progress"
    >
      <div
        ref={railRef}
        className="relative overflow-x-auto no-scrollbar"
        style={{ scrollbarWidth: "none" }}
      >
        {/* Track container — min-width forces the rail to keep node spacing
            reasonable when the category list is long; on narrow viewports
            the outer div scrolls to compensate. */}
        <div
          className="relative h-11 flex items-center"
          style={{ minWidth: `${Math.max(320, (total + 1) * 72)}px` }}
        >
          {/* Base rail — bumped to primary/20 (was bg-muted) so the track
              stays visible on light backgrounds where muted disappeared. */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-primary/20" />
          {/* Progress fill */}
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-primary transition-[width] duration-500 ease-out"
            style={{ width: `${progress * 100}%` }}
          />

          {/* Category dots */}
          {sections.map((s, i) => {
            const left = ((i + 0.5) / (total + 1)) * 100;
            const isDone = i < activeIndex;
            const isActive = i === activeIndex;
            return (
              <button
                key={s.category_id || `t-${i}`}
                type="button"
                onClick={() => onSelect(i)}
                data-testid={`products-carousel-timeline-node-${i}`}
                data-active-pill={isActive ? "true" : undefined}
                aria-current={isActive ? "step" : undefined}
                title={s.name}
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group focus:outline-none ${isActive ? "z-20" : "z-10"}`}
                style={{ left: `${left}%` }}
              >
                {isActive ? (
                  // Active pill — announces where we are without needing
                  // labels under every dot.
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold shadow-md whitespace-nowrap ring-2 ring-primary/25 transition-transform duration-300 ease-out"
                    style={{ transform: "scale(1)" }}
                  >
                    <span className="opacity-70 tabular-nums">
                      {i + 1}/{total}
                    </span>
                    <span className="max-w-[140px] truncate">{s.name}</span>
                  </span>
                ) : (
                  // Inactive dot — hollow for upcoming, solid for done.
                  // Hover shows the category name as a floating chip.
                  <>
                    <span
                      className={`block h-3 w-3 rounded-full border-2 transition-all group-hover:scale-125 ${
                        isDone
                          ? "bg-primary border-primary"
                          : "bg-background border-primary/60 group-hover:border-primary"
                      }`}
                    />
                    <span
                      className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-8 opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded bg-foreground text-background shadow"
                    >
                      {s.name}
                    </span>
                  </>
                )}
              </button>
            );
          })}

          {/* Checkout terminal — always anchored at the right end of the rail.
              Uses right-anchoring (translate-x-0) so the full label fits
              inside the container instead of hanging off the edge. When the
              basket is empty we render a disabled span so buyers can't
              wander into an empty checkout — clicking a full basket routes
              normally. */}
          {basketCount > 0 ? (
            <Link
              to="/checkout"
              data-testid="products-carousel-timeline-checkout"
              title={`Checkout (${basketCount} items)`}
              className="absolute top-1/2 -translate-y-1/2 group z-20"
              style={{ right: 0 }}
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all group-hover:scale-105 bg-primary text-primary-foreground shadow-md ring-2 ring-primary/30">
                <CreditCard className="h-3.5 w-3.5" />
                <span>Checkout · {basketCount}</span>
              </span>
            </Link>
          ) : (
            <span
              data-testid="products-carousel-timeline-checkout"
              data-disabled="true"
              aria-disabled="true"
              title="Add an item to your basket to continue to checkout"
              className="absolute top-1/2 -translate-y-1/2 z-20 cursor-not-allowed opacity-50 select-none"
              style={{ right: 0 }}
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap bg-muted text-muted-foreground border-2 border-dashed border-muted-foreground/30">
                <CreditCard className="h-3.5 w-3.5" />
                <span>Checkout</span>
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Sr-only label — the visible pill already shows the current step,
          but screen readers benefit from an explicit summary. */}
      <span className="sr-only">
        Step {activeIndex + 1} of {total}: {activeName}
      </span>
    </div>
  );
}
